import React, { useState, useRef, useEffect } from 'react';
import client from '../../../shared/api/client';
import { toastError } from '../../../shared/toastHelper';

const FundDropdown = ({ fundId, setFundId, fundList, setFundList, team }) => {
  const [fundDropdownOpen, setFundDropdownOpen] = useState(false);
  const [addingFund, setAddingFund] = useState(false);
  const [newFundName, setNewFundName] = useState('');
  const [newFundCode, setNewFundCode] = useState('');
  const [newFundCodeTouched, setNewFundCodeTouched] = useState(false);
  const [newFundGeneralLedgerCodeId, setNewFundGeneralLedgerCodeId] = useState('');
  const [generalLedgerCodes, setGeneralLedgerCodes] = useState([]);
  const [fundSaving, setFundSaving] = useState(false);
  const [fundCodeSaving, setFundCodeSaving] = useState(false);
  const fundDropdownRef = useRef(null);

  const slugify = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const resetAddFund = () => {
    setAddingFund(false);
    setNewFundName('');
    setNewFundCode('');
    setNewFundCodeTouched(false);
    setNewFundGeneralLedgerCodeId('');
  };

  const onNameChange = (value) => {
    setNewFundName(value);
    if (!newFundCodeTouched) setNewFundCode(slugify(value));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fundDropdownRef.current && !fundDropdownRef.current.contains(e.target)) {
        setFundDropdownOpen(false);
        setAddingFund(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!team.can_access_general_ledger_codes) return undefined;

    let ignore = false;
    client.get(`/teams/${team.id}/general_ledger_codes`)
      .then(({ data }) => { if (!ignore) setGeneralLedgerCodes(data || []); })
      .catch(() => {
        if (ignore) return;
        setGeneralLedgerCodes([]);
        toastError('Could not load GL codes. Reopen this form to try again.');
      });

    return () => { ignore = true; };
  }, [team.id, team.can_access_general_ledger_codes]);

  const selectedFund = fundList.find((fund) => fund.id == fundId);

  const generalLedgerCodeOptions = generalLedgerCodes.map((generalLedgerCode) => (
    <option key={generalLedgerCode.id} value={generalLedgerCode.id}>
      {generalLedgerCode.code} — {generalLedgerCode.name}
    </option>
  ));

  const handleSelectedFundCodeChange = async (value) => {
    if (fundCodeSaving) return;

    const generalLedgerCodeId = value || null;
    setFundCodeSaving(true);
    try {
      await client.patch(`/teams/${team.id}/funds/${fundId}`, {
        fund: { general_ledger_code_id: generalLedgerCodeId }
      });
      setFundList(prev => prev.map((fund) => (
        fund.id == fundId ? { ...fund, general_ledger_code_id: generalLedgerCodeId } : fund
      )));
    } catch (err) {
      const msgs = err.response?.data?.errors;
      toastError(msgs ? msgs.join(', ') : 'Failed to update fund');
    } finally {
      setFundCodeSaving(false);
    }
  };

  const handleCreateFund = async () => {
    if (!newFundName.trim() || !newFundCode.trim() || fundSaving) return;
    setFundSaving(true);
    try {
      const { data } = await client.post(`/teams/${team.id}/funds`, {
        fund: {
          name: newFundName.trim(),
          fund_code: newFundCode.trim(),
          general_ledger_code_id: newFundGeneralLedgerCodeId || null
        }
      });
      setFundList(prev => [...prev, data]);
      setFundId(data.id);
      resetAddFund();
    } catch (err) {
      const msgs = err.response?.data?.errors;
      toastError(msgs ? msgs.join(', ') : 'Failed to create fund');
    } finally {
      setFundSaving(false);
    }
  };

  return (
    <>
    <div className="gc-field">
      <label className="gc-label">Default Fund</label>
      <div className="gc-dropdown" ref={fundDropdownRef}>
        <button
          type="button"
          className={`gc-dropdown-trigger${fundDropdownOpen ? ' gc-dropdown-trigger--open' : ''}`}
          onClick={() => { setFundDropdownOpen(!fundDropdownOpen); setAddingFund(false); }}
        >
          <span className={fundId ? 'gc-dropdown-value' : 'gc-dropdown-placeholder'}>
            {fundId
              ? (() => { const f = fundList.find(f => f.id == fundId); return f ? f.name : 'None'; })()
              : 'None'}
          </span>
          <svg className="gc-dropdown-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9L12 15L18 9" />
          </svg>
        </button>
        {fundDropdownOpen && (
          <div className="gc-dropdown-menu">
            <div
              className={`gc-dropdown-item${!fundId ? ' gc-dropdown-item--selected' : ''}`}
              onClick={() => { setFundId(''); setFundDropdownOpen(false); }}
            >
              <span>None</span>
              {!fundId && (
                <svg className="gc-dropdown-check" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17L4 12" />
                </svg>
              )}
            </div>
            {fundList.map((fund) => (
              <div
                key={fund.id}
                className={`gc-dropdown-item${fundId == fund.id ? ' gc-dropdown-item--selected' : ''}`}
                onClick={() => { setFundId(fund.id); setFundDropdownOpen(false); }}
              >
                <span>{fund.name}</span>
                {fundId == fund.id && (
                  <svg className="gc-dropdown-check" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17L4 12" />
                  </svg>
                )}
              </div>
            ))}
            {!addingFund ? (
              <div className="gc-dropdown-item gc-dropdown-add" onClick={(e) => { e.stopPropagation(); setAddingFund(true); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5V19M5 12H19" />
                </svg>
                <span>Add Fund</span>
              </div>
            ) : (
              <div className="gc-dropdown-add-form" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  className="gc-input"
                  placeholder="Fund name"
                  value={newFundName}
                  onChange={(e) => onNameChange(e.target.value)}
                  autoFocus
                />
                <input
                  type="text"
                  className="gc-input"
                  placeholder="Fund code"
                  value={newFundCode}
                  onChange={(e) => { setNewFundCode(e.target.value); setNewFundCodeTouched(true); }}
                />
                {team.can_access_general_ledger_codes && (
                  <select
                    id="new-fund-general-ledger-code"
                    className="gc-select"
                    value={newFundGeneralLedgerCodeId}
                    onChange={(e) => setNewFundGeneralLedgerCodeId(e.target.value)}
                  >
                    <option value="">No GL code</option>
                    {generalLedgerCodeOptions}
                  </select>
                )}
                <div className="gc-dropdown-add-actions">
                  <button type="button" className="btn btn-setlist-secondary btn-sm" onClick={resetAddFund}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-setlist-primary btn-sm" onClick={handleCreateFund} disabled={!newFundName.trim() || !newFundCode.trim() || fundSaving}>
                    {fundSaving ? 'Saving...' : 'Add'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    {team.can_access_general_ledger_codes && selectedFund && (
      <div className="gc-field">
        <label className="gc-label" htmlFor="fund-general-ledger-code">GL Code</label>
        <select
          id="fund-general-ledger-code"
          className="gc-select"
          value={selectedFund.general_ledger_code_id || ''}
          disabled={fundCodeSaving}
          onChange={(e) => handleSelectedFundCodeChange(e.target.value)}
        >
          <option value="">No GL code</option>
          {generalLedgerCodeOptions}
        </select>
        <p className="gc-hint">
          Donations to {selectedFund.name} post to this account. The code belongs to the
          fund, so it applies everywhere the fund is used, not just this campaign.
        </p>
      </div>
    )}
    </>
  );
};

export default FundDropdown;
